import {put, call} from 'redux-saga/effects';
import Campaign from "../../entities/campaign";
import * as Actions from "../actions/campaignActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type CampaignAction = {
    campaignId: string;
    campaign: Campaign;
    campaigns: Campaign[];
};

export function* fetchOneCampaign(action: CampaignAction) {
	try {
		const campaign: Campaign = yield call(api.findOne, 'campaign', action.campaignId);

		// Now we can update the user interface
		yield put({type: Actions.CAMPAIGN_FETCH_ONE_SUCCESS, campaignId: action.campaignId, campaign});
	}
	catch(err) {
		yield put({type: Actions.CAMPAIGN_FETCH_ONE_FAILURE, campaignId: action.campaignId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {CampaignAction} action
 */
export function* saveCampaign(action: CampaignAction) {
    // console.debug('⮂ CAMPAIGN_SAVE saga', action.campaign?.id);
    
	if (action.campaign === undefined || action.campaign === null) {
		console.error('saveCampaign received a null');
		throw new Error('saveCampaign received a null');
	}

	if (!(action.campaign instanceof Campaign)) {
		action.campaign = new Campaign(action.campaign);
	}

	try {
		// Save to IndexedDB
		const addResult = yield call(idb.add, 'campaign', action.campaign);
		if (addResult.isErr()) {
			console.error(`⮂ saveCampaign: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.CAMPAIGN_SAVE_FAILURE, campaign: action.campaign, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.CAMPAIGN_SAVE, campaign: action.campaign});

		// Delete this entry from the cache (if there is one). Use uncacheByEntityId so we
		// catch entries regardless of which parentId the writer used (zero for grid views,
		// topLevelId for sub-task views, etc.) — the prior uncacheItem call passed entityId
		// as parentId, which never matched what writers store under, so entries leaked.
		yield call(window.storageWorker.uncacheByEntityId, getCurrentFirmId(), getCurrentUserId(), 'campaign', action.campaign.id);

		// Let the queue know to process
		yield call(processQueue);
	}
	catch(err) {
		yield put({type: Actions.CAMPAIGN_SAVE_FAILURE, campaign: action.campaign, status: 500, err});

		console.error('Error saving to database', err);
	}
}

/**
 * TODO: Update for saveMany
 * @param {CampaignAction} action
 * @param {Campaign[]} action.campaigns
 */
export function* saveManyCampaigns(action: CampaignAction) {
    // console.debug('⮂ CAMPAIGN_SAVE_MANY saga', action.campaigns.map(campaign => campaign.id).join(','));
    
    if (!Array.isArray(action.campaigns)) {
        console.error('saveManyCampaigns expected an array, received', action.campaigns);
        throw new Error('saveManyCampaigns expected an array');
    }

	try {
		const toSave: Campaign[] = [];

		// Save each item to IndexedDB
		for(let i = 0; i < action.campaigns.length; i++) {
			const campaign = action.campaigns[i];
			campaign.persisted = false;
			toSave.push(campaign);
		}

		const addResult = yield call(idb.add, 'campaign', toSave);
		if (addResult.isErr()) {
			console.error(`⮂ saveManyCampaigns: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.CAMPAIGN_SAVE_MANY_FAILURE, campaigns: action.campaigns, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.CAMPAIGN_SAVE_MANY, campaigns: toSave});

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		yield put({type: Actions.CAMPAIGN_SAVE_MANY_FAILURE, campaigns: action.campaigns, status: 500, err});

		console.error(`Error saving ${action.campaigns.length} campaigns to database`, err);
	}
}

/**
 * @param {CampaignAction} action
 */
export function* removeCampaign(action: CampaignAction) {
    const userId = getCurrentUserId();
	if (!userId || action.campaign === undefined || action.campaign === null || !(action.campaign instanceof Campaign)) {
			console.error('removeCampaign expected type Campaign, received', action.campaign);
			throw new Error('removeCampaign expected type Campaign');
		}

	try {
		// Mark as deleted
		action.campaign.status = 'D';
		action.campaign.addedOn = newHRTimestamp();
		action.campaign.addedBy = userId;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'campaign', action.campaign);
		if (addResult.isErr()) {
			console.error(`⮂ removeCampaign: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.CAMPAIGN_REMOVE_FAILURE, campaign: action.campaign, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.CAMPAIGN_REMOVE, campaign: action.campaign});

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		yield put({type: Actions.CAMPAIGN_REMOVE_FAILURE, campaign: action.campaign, status: 500, err});

		console.error('Error deleting from database', err);
	}
}

/**
	* Restore the last version of this Campaign.
	*
	* @param {CampaignAction} action
	*/
export function* restoreCampaign(action: CampaignAction) {
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreCampaign expected getCurrentUserId() to return a value');
        return;
    }
    
	if (action.campaign === undefined || action.campaign === null) {
        console.error('restoreCampaign expected a Campaign, received null');
        throw new Error('restoreCampaign expected a Campaign, received null');
    }
    else if (!(action.campaign instanceof Campaign)) action.campaign = new Campaign(action.campaign); 
    
	try {
		// Mark as restored
		action.campaign.status = 'U';
		action.campaign.addedOn = newHRTimestamp();
		action.campaign.addedBy = userId;
		action.campaign.restore = true;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'campaign', action.campaign);
		if (addResult.isErr()) {
			console.error(`⮂ restoreCampaign: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.CAMPAIGN_RESTORE_FAILURE, campaign: action.campaign, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.CAMPAIGN_RESTORE, campaign: action.campaign});
	}
	catch(err) {
		yield put({type: Actions.CAMPAIGN_RESTORE_FAILURE, campaign: action.campaign, status: 500, err});

		console.error('Error restoring from database', err);
	}
}